/** Settings > Routes (tracker item 57): named dispatch routes, technician defaults and dated coverage. */
import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createAPIClient, IDispatchApi, IUsersApi } from '../../api';
import * as types from '../../api/types';
import { Toast } from '../../components/Toast';
import { inputClass, labelClass, primaryButtonClass, rowActionClass, rowDangerActionClass, secondaryButtonClass } from '../../components/formStyles';
import { getErrorMessage } from '../../utils/apiError';

function todayLocal(): string {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function RoutesPage() {
  const api = useMemo(() => createAPIClient() as IDispatchApi & IUsersApi, []);
  const [routes, setRoutes] = useState<types.DispatchRoute[]>([]);
  const [people, setPeople] = useState<types.AssignableUser[]>([]);
  const [userId, setUserId] = useState('');
  const [date, setDate] = useState(todayLocal);
  const [usualRouteIds, setUsualRouteIds] = useState<string[]>([]);
  const [assignments, setAssignments] = useState<types.RouteAssignment[]>([]);
  const [loadingPerson, setLoadingPerson] = useState(false);
  const personRequest = useRef(0);
  const [routeToAssign, setRouteToAssign] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadRoutes = useCallback(async () => setRoutes(await api.listDispatchRoutes()), [api]);
  const loadPersonRoutes = useCallback(async (id: string, selectedDate: string) => {
    const request = ++personRequest.current;
    setUsualRouteIds([]);
    setAssignments([]);
    if (!id || !selectedDate) { setLoadingPerson(false); return; }
    setLoadingPerson(true);
    try {
      const [usual, dated] = await Promise.all([
        api.getTechnicianRoutes(id),
        api.listRouteAssignments({ userId: id, date: selectedDate }),
      ]);
      if (request === personRequest.current) {
        setUsualRouteIds(usual);
        setAssignments(dated);
      }
    } catch (err) {
      if (request === personRequest.current) setError(getErrorMessage(err, 'Could not load technician routes'));
    } finally {
      if (request === personRequest.current) setLoadingPerson(false);
    }
  }, [api]);

  useEffect(() => {
    Promise.all([loadRoutes(), api.listAssignableUsers()])
      .then(([, staff]) => {
        const techs = staff.filter((person) => person.role === types.UserRole.TECHNICIAN);
        setPeople(techs);
        if (techs[0]) setUserId(techs[0].id);
      })
      .catch((err) => setError(getErrorMessage(err, 'Could not load routes and technicians')));
  }, [api, loadRoutes]);
  useEffect(() => { loadPersonRoutes(userId, date); }, [loadPersonRoutes, userId, date]);

  const act = async (success: string, action: () => Promise<void>) => {
    setBusy(true); setError(null); setNotice(null);
    try { await action(); setNotice(success); }
    catch (err) { setError(getErrorMessage(err, 'Could not save route settings')); }
    finally { setBusy(false); }
  };

  const createRoute = async (event: FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) { setError('Enter a route name.'); return; }
    await act('Route created.', async () => { await api.createDispatchRoute({ name }); setNewName(''); await loadRoutes(); });
  };

  const renameRoute = async (route: types.DispatchRoute) => {
    const name = window.prompt('Route name', route.name)?.trim();
    if (!name || name === route.name) return;
    await act('Route renamed.', async () => { await api.updateDispatchRoute(route.id, { name }); await loadRoutes(); });
  };

  const toggleRoute = async (route: types.DispatchRoute) => {
    await act(route.isActive === false ? 'Route activated.' : 'Route deactivated.', async () => {
      await api.updateDispatchRoute(route.id, { isActive: route.isActive === false }); await loadRoutes();
    });
  };

  const deleteRoute = async (route: types.DispatchRoute) => {
    if (!window.confirm(`Delete route “${route.name}”?`)) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      await api.deleteDispatchRoute(route.id);
      await loadRoutes(); await loadPersonRoutes(userId, date);
      setNotice('Route deleted.');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 409
        ? 'This route is in use and cannot be deleted. Deactivate it instead.'
        : getErrorMessage(err, 'Could not delete route'));
    } finally { setBusy(false); }
  };

  const saveUsualRoutes = async () => {
    if (!userId || loadingPerson) return;
    if (usualRouteIds.some((id) => !activeRoutes.some((route) => route.id === id))) {
      setError('Remove inactive routes before saving.');
      return;
    }
    await act('Usual routes saved.', async () => { setUsualRouteIds(await api.setTechnicianRoutes(userId, usualRouteIds)); });
  };

  const addCoverage = async (event: FormEvent) => {
    event.preventDefault();
    if (!userId || !date || !routeToAssign) { setError('Choose a date and route.'); return; }
    await act('Route coverage added.', async () => {
      await api.createRouteAssignment({ userId, routeId: routeToAssign, date });
      setAssignments(await api.listRouteAssignments({ userId, date }));
    });
  };

  const removeCoverage = async (assignment: types.RouteAssignment) => {
    await act('Route coverage removed.', async () => {
      await api.deleteRouteAssignment(assignment.id);
      setAssignments((rows) => rows.filter((row) => row.id !== assignment.id));
    });
  };

  const activeRoutes = routes.filter((route) => route.isActive !== false);
  const inactiveSelectedRoutes = routes.filter((route) => route.isActive === false && usualRouteIds.includes(route.id));
  const routeName = (id: string) => routes.find((route) => route.id === id)?.name ?? 'Deleted route';

  return <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
    <h1 className="mb-2 text-2xl font-bold text-gray-900">Routes</h1>
    <p className="mb-6 text-sm text-gray-600">Create named service routes, set each technician’s usual routes, and add date-specific coverage.</p>
    {notice && <Toast kind="success">{notice}</Toast>}{error && <Toast kind="error">{error}</Toast>}

    <section className="mb-6 rounded-lg bg-white p-5 shadow" aria-label="Named routes">
      <h2 className="mb-3 text-lg font-semibold">Named routes</h2>
      <form className="mb-4 flex flex-col gap-3 sm:flex-row" onSubmit={createRoute}>
        <div className="flex-1"><label htmlFor="new-route-name" className={labelClass}>Route name</label><input id="new-route-name" className={inputClass} maxLength={100} value={newName} disabled={busy} onChange={(e) => setNewName(e.target.value)} /></div>
        <div className="sm:self-end"><button type="submit" className={primaryButtonClass} disabled={busy}>Create route</button></div>
      </form>
      {routes.length === 0 ? <p className="text-sm text-gray-600">No routes yet.</p> : <ul className="divide-y divide-gray-100">
        {routes.map((route) => <li key={route.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
          <span className={`font-medium ${route.isActive === false ? 'text-gray-500' : 'text-gray-900'}`}>{route.name} <span className="ml-1 text-xs font-normal text-gray-500">{route.isActive === false ? 'Inactive' : 'Active'}</span></span>
          <span className="flex gap-4"><button type="button" className={rowActionClass} disabled={busy} onClick={() => renameRoute(route)}>Rename</button>
            <button type="button" className={rowActionClass} disabled={busy} onClick={() => toggleRoute(route)}>{route.isActive === false ? 'Activate' : 'Deactivate'}</button>
            <button type="button" className={rowDangerActionClass} disabled={busy} onClick={() => deleteRoute(route)}>Delete</button></span>
        </li>)}
      </ul>}
    </section>

    <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div><label htmlFor="route-technician" className={labelClass}>Technician</label><select id="route-technician" className={inputClass} value={userId} onChange={(e) => setUserId(e.target.value)} disabled={!people.length}>
        {people.length === 0 && <option value="">No technicians found</option>}{people.map((person) => <option key={person.id} value={person.id}>{person.email}</option>)}
      </select></div>
      <div><label htmlFor="route-date" className={labelClass}>Coverage date</label><input id="route-date" type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} /></div>
    </div>

    {userId && <>
      <section className="mb-6 rounded-lg bg-white p-5 shadow" aria-label="Usual routes">
        <h2 className="mb-1 text-lg font-semibold">Usual routes</h2><p className="mb-3 text-sm text-gray-600">Routes this technician normally covers. Save changes when finished.</p>
        {activeRoutes.length === 0 && inactiveSelectedRoutes.length === 0 ? <p className="text-sm text-gray-600">Create an active route above to assign usual routes.</p> : <fieldset className="space-y-2" disabled={busy || loadingPerson}>
          <legend className="sr-only">Select usual routes</legend>{activeRoutes.map((route) => <label key={route.id} className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={usualRouteIds.includes(route.id)} onChange={(e) => setUsualRouteIds((ids) => e.target.checked ? [...ids, route.id] : ids.filter((id) => id !== route.id))} />{route.name}
          </label>)}
          {inactiveSelectedRoutes.map((route) => <label key={route.id} className="flex items-center gap-2 text-sm text-gray-500">
            <input type="checkbox" checked onChange={() => setUsualRouteIds((ids) => ids.filter((id) => id !== route.id))} />{route.name} (inactive — remove before saving)
          </label>)}
        </fieldset>}
        <div className="mt-4 flex justify-end"><button type="button" className={secondaryButtonClass} disabled={busy || loadingPerson} onClick={saveUsualRoutes}>Save usual routes</button></div>
      </section>

      <section className="rounded-lg bg-white p-5 shadow" aria-label="Date-specific route coverage">
        <h2 className="mb-1 text-lg font-semibold">Date-specific coverage</h2><p className="mb-3 text-sm text-gray-600">Add extra routes for {date}.</p>
        <form className="mb-4 flex flex-col gap-3 sm:flex-row" onSubmit={addCoverage}>
          <div className="flex-1"><label htmlFor="coverage-route" className={labelClass}>Route</label><select id="coverage-route" className={inputClass} value={routeToAssign} onChange={(e) => setRouteToAssign(e.target.value)} disabled={!activeRoutes.length || busy}>
            <option value="">Choose a route</option>{activeRoutes.map((route) => <option key={route.id} value={route.id}>{route.name}</option>)}
          </select></div><div className="sm:self-end"><button type="submit" className={secondaryButtonClass} disabled={busy || loadingPerson || !routeToAssign}>Add coverage</button></div>
        </form>
        {assignments.length === 0 ? <p className="text-sm text-gray-600">No extra route coverage for this date.</p> : <ul className="divide-y divide-gray-100">{assignments.map((assignment) => <li key={assignment.id} className="flex items-center justify-between gap-3 py-2 text-sm">
          <span>{routeName(assignment.routeId)}</span><button type="button" className={rowDangerActionClass} disabled={busy || loadingPerson} onClick={() => removeCoverage(assignment)}>Remove</button>
        </li>)}</ul>}
      </section>
    </>}
  </main>;
}
